import { Box, Button, Chip, Grid, makeStyles, Typography } from "@material-ui/core";
import FileSaver from "file-saver";
import React, { useEffect, useState } from "react";
import { useDispatch } from 'react-redux';
import AlertDialog from "../../components/alert-dialog/AlertDialog";
import FileDrop from "../../components/file-drop/FileDrop";
import { HeaderBlock } from "../../components/header-block/HeaderBlock";
// Actions
import { Loading } from "../../components/loading/Loading";
// Styles
import PageContainer from "../../components/page-container/PageContainer";
import { checkStatusCSV, getCSVBlob, uploadCSV } from "../../state/actions/CSVActions";
import { FileType } from "../../state/constants/FileType";
import { useTranslation } from "react-i18next";
import { getCSVType, CSVType } from "../../state/constants/CSVType";
import { getIcon } from "icon/icon";
import { FixMeLater } from "types/FixMeLaterType";

interface Props {
    type: CSVType,
    urlOverride: URLOverride
}

interface URLOverride {
    get: string,
    post: string
}

interface CSVResponse {
    createdTime: Date,
    customStatus: string,
    input: { FileName: string, UserId: string, Type: number }
    instanceId: string,
    lastUpdatedTime: Date,
    name: string,
    output: boolean | string | null,
    runtimeStatus: string
}

const useStyles = makeStyles({
    dropArea: {
        height: "300px",
        width: "250px"
    },

    dropContainer: {
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        fontSize: "calc(10px + 2vmin)",
        padding: "16px"
    },

    buttonStyle: {
        width: "250px",
        margin: "8px !important",
        alignItems: "flex-start",
        justifyContent: "center",
        animation: "moveAcross 1s 1"
    },

    "@keyframes moveAcross": {
        "0%": {
            opacity: "0"
        },
        "100%": {
            opacity: "1"
        }
    },

    icon: {
        height: "25px",
        filter: "invert(100%)"
    }
})

export default function CSVGenericPage(props: Props) {

    const classes = useStyles()
    const [t, i18n] = useTranslation()
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false)
    const [file, setFile] = useState<File[] | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [status, setStatus] = useState<string | null>(null)
    const [errors, setErrors] = useState<CSVResponse["output"]>(null)
    const [fetchStatus, setFetchStatus] = useState(true)

    const type: CSVType = getCSVType()[props.type.key]

    const get = props.urlOverride?.get
    const post = props.urlOverride?.post

    /**
     * Effect for resetting FileDrop
     * Triggered by changes to types key
     */
    useEffect(() => {
        setLoading(false)
        setFile(null)
        setDialogOpen(false)
        setStatus(null)
        setErrors(null)

        setFetchStatus(true)

        return () => {
            setLoading(false)
            setFile(null)
            setDialogOpen(false)
            setStatus(null)
            setErrors(null)
        }
    }, [type.key]);

    /**
     * Effect for fetching status of Upload
     * Triggered by changes to fetchStatus flag
     */
    useEffect(() => {
        if (fetchStatus) {
            setStatus(null)
            setErrors(null)
            dispatch(checkStatusCSV(type.apiType, (response: CSVResponse | null, error: FixMeLater | null) => {
                if (error != null && error.response?.data?.name == "UploadCSV") {
                    // If failed, show errors.
                    setStatus(error.response?.data?.runtimeStatus)
                    setErrors(error.response?.data?.output)
                } else if (response != null) {
                    console.log(response);
                    // update based on status
                    setStatus(response.runtimeStatus)
                } else {
                    setStatus("Ready")
                }
                setFetchStatus(false)
            }))
        }
    }, [fetchStatus])

    /**
     * Handle the file drop in the FileDrop component
     * @param file The files uploaded
     */
    function handleDrop(file: File[]) {
        if (file == null) return
        setFile(file)
    }

    /**
     * Handle the fetch of an existing file
     */
    function handleDownloadOnClick() {
        dispatch(getCSVBlob(type.apiType, (blob: Blob) => {
            if (blob) {
                FileSaver.saveAs(blob, `${type.apiType}.csv`);
            }
        }, props.urlOverride != null ? props.urlOverride.get : null))
    }

    /**
     * Handle the upload of the file
     */
    function handlePerformUpload() {
        // Only send the first file
        dispatch(uploadCSV(file != null ? file[0] : null,
            type.apiType, () => {
                console.log("Upload")
            }, props.urlOverride != null ? props.urlOverride.post : null))
        handleClose()
    }

    /**
     * Handle status check for CSV upload 
     */
    function handleCheckStatus() {
        setFetchStatus(true)
    }

    /**
     * Handle modal open
     */
    function handleClickOpen() {
        setDialogOpen(true)
    }

    /**
     * Handle modal close
     */
    function handleClose() {
        setDialogOpen(false)
    }

    /**
     * Render the Filedrop component
     */
    function renderData() {
        return (
            <Box className={classes.dropContainer}>
                <Grid container spacing={1} alignItems="center" justify="center">
                    <Grid item xs={12}>
                        <FileDrop
                            enforceTypes={[FileType.csv]}
                            enforceTypesMessage={t("csv.file-drop-enforce-type")}
                            message={t("csv.file-drop-message", { title: type.title })}
                            onFilesAdded={handleDrop}
                            file={file}
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            disabled={file == null}
                            className={classes.buttonStyle}
                            variant="contained"
                            color="primary"
                            onClick={handleClickOpen}
                            startIcon={getIcon("arrow-up-circle.svg", classes.icon)}
                        >
                            {t('csv.upload')}
                        </Button>
                    </Grid>
                    <Grid item>

                        {
                            // If we have a post override or no overrides, show backup button
                            get || (!post && !get) ?
                                <Button
                                    className={classes.buttonStyle}
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownloadOnClick}
                                    startIcon={getIcon("arrow-down-circle.svg", classes.icon)}
                                >
                                    {t('csv.download')}
                                </Button> :
                                null
                        }
                    </Grid>
                    <Grid item>
                        <Button
                            className={classes.buttonStyle}
                            variant="contained"
                            color="primary"
                            onClick={handleCheckStatus}
                            startIcon={getIcon("spinner-arrow.svg", classes.icon)}
                        >
                            {t('csv.check-status')}
                        </Button>
                    </Grid>
                    {
                        errors != null ?
                            < Grid item xs={12}>
                                <Typography variant="h6">
                                    {t('csv.upload-errors')}
                                </Typography>
                                <Typography variant="body1">
                                    {errors}
                                </Typography>
                            </Grid> :
                            null
                    }
                </Grid>
            </Box >
        );
    }

    /**
     * Generate the button options for the upload warning label
     */
    function generateOptions() {
        const options = []
        options.push({
            key: "Cancel",
            action: handleClose,
            label: t("common.cancel")
        })

        // If we have a get override or no overrides, show backup button
        if (get || (!post && !get)) {
            options.push({
                key: "Backup",
                action: handleDownloadOnClick,
                label: t("common.backup")
            })
        }

        options.push({
            key: "Upload",
            action: handlePerformUpload,
            label: t("common.okay")
        })
        return options
    }

    return (
        <Box>
            <HeaderBlock
                title={type.title}
                right={
                    status != null
                        ? <Chip
                            label={t('csv.upload-status', { status: status })}
                            variant="outlined"
                            color={errors != null ? "primary" : "default"}
                        />
                        : null
                }
            />
            <PageContainer>
                {loading ? <Loading /> : renderData()}
            </PageContainer>
            <AlertDialog
                isOpen={dialogOpen}
                handleClose={handleClose}
                title={t("csv.override-csv", { title: type.title })}
                description={t("csv.override-csv-description")}
                options={generateOptions()}
            />
        </Box>
    );
}